import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Navigate, Outlet, useSearchParams } from 'react-router-dom';

import { RootState } from '../ducks/store';
import { closeHubSpotChat, isPlatformAdmin, isTeamManager } from '../utils/functions';
import NavRail from './common/NavColumn/NavRail';
import OutletWrapper from './common/OutletWrapper/OutletWrapper';
import { showModal } from '../ducks/modal';
import MaintenanceNoticeModal from './common/modals/MaintenanceNoticeModal/MaintenanceNoticeModal';
import { ModalTypes } from '../utils/constants';
import InactivityModal from './common/modals/InactivityModal/InactivityModal';
import { useCookieConsent } from '../hooks/useCookieConsent';

const win: IWindow = window;

const checkAndAddHubSpotClose = () => {
  // Check to see if hubspot container exists, and if so, append close icon
  const hubSpotDiv = document.getElementById('hubspot-messages-iframe-container');
  if (hubSpotDiv) {
    // Manually set height to 638px because the widget doesn't get the height
    // set due to the transform on #hubspot-messages-iframe-container.widget-align-right
    // for some reason, likely because there's something in the script
    // checking to see if the button we are hiding with the translate is visible,
    // and if it isn't then it doesn't set the height.
    hubSpotDiv.style.height = '638px';
    let wrapper = document.getElementById('hubspot-close-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.id = 'hubspot-close-wrapper';
      wrapper.classList.add('hubspot-chat-close');
      wrapper.onclick = () => {
        if (win.HubSpotConversations) {
          closeHubSpotChat();
          wrapper?.classList.add('hide-icon');
        }
      };
      wrapper.innerHTML = '<span class="material-icons">close</span>';

      hubSpotDiv.append(wrapper);
    } else {
      wrapper.classList.remove('hide-icon');
    }
  }
};

const useHubSpotChat = (isAuthenticated: boolean, hasAnalyticsConsent: boolean) => {
  useEffect(() => {
    // this is meant to add close icon to Hubspot Chat. This useEffect ties
    // into the window MessageEvent handler and adds it when the
    // widget is initialized
    const onMessage = (e: MessageEvent<any>) => {
      let data;
      try {
        // Attempt to parse strings as JSON
        data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      } catch (ex) {
        if (!data && e.data) {
          data = e.data;
        }
      }

      if (data.type === 'open-change') {
        if (data.data?.isOpen) {
          checkAndAddHubSpotClose();
        } else {
          document.getElementById('hubspot-close-wrapper')?.classList.add('hide-icon');
        }
      }
    };

    // If the user is authenticated, inject the Hubspot script
    if (isAuthenticated) {
      // Check if consent has been accepted for analytics cookies,
      // otherwise, don't load the HubSpot script because it will display
      // a poorly styled and positioned tooltip in the bottom right corner
      // stating that consent is required.

      if (hasAnalyticsConsent) {
        if (win.HubSpotConversations?.widget) {
          console.info('HubSpot widget already exists, refreshing ...');
          win.HubSpotConversations.widget.refresh();
        } else {
          console.info('Adding HubSpot widget');
          const script = document.createElement('script');
          script.src = '//js.hs-scripts.com/5653847.js';
          script.async = true;
          script.id = 'hs-script-loader';
          script.type = 'text/javascript';
          script.defer = true;
          const root = document.getElementById('root');
          root?.appendChild(script);

          win.addEventListener('message', onMessage);
        }
      } else {
        // Clean up the HubSpot widget if it exists
        console.info('Removing HubSpot widget due to lack of analytics consent');
        win.HubSpotConversations?.widget?.remove();
      }
    }
    // We only want to run this on load, so the empty deps is fine
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnalyticsConsent]);
};

const RequireAuth = ({
  children,
  isSettings,
  checkManager,
  checkAdmin,
}: {
  children?: JSX.Element;
  isSettings?: boolean;
  checkManager?: boolean;
  checkAdmin?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    isAuthenticated,
    isAdmin,
    isManager,
    forcePasswordReset,
    forceRegistration,
    unmetRequirements,
    modalType,
    maintenanceNotified,
    userId,
  } = useSelector((state: RootState) => {
    const { isAuthenticated, authenticatedUser, maintenanceNotified } = state.auth;
    const { showType } = state.modal;
    const isAdmin = isPlatformAdmin(authenticatedUser);
    const isManager = isTeamManager(authenticatedUser);
    return {
      isAuthenticated,
      isAdmin,
      isManager,
      forcePasswordReset: authenticatedUser?.forcePasswordReset,
      forceRegistration: authenticatedUser?.forceRegistration,
      unmetRequirements: authenticatedUser?.unmetRequirements,
      modalType: showType,
      maintenanceNotified,
      userId: authenticatedUser?._id,
    };
  });

  const { hasAnalyticsConsent } = useCookieConsent();
  useHubSpotChat(!!isAuthenticated, hasAnalyticsConsent);

  useEffect(() => {
    if (isAuthenticated && userId) {
      searchParams.set('uid', userId);
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, isAuthenticated, userId]);

  if (isAuthenticated) {
    if ((forcePasswordReset || forceRegistration || unmetRequirements) && !isSettings) {
      return <Navigate to="/settings" state={{ from: location }} replace />;
    } else if (!checkAdmin || isAdmin) {
      if (!isAdmin) {
        return (
          <>
            {children || <Outlet />}
            <MaintenanceNoticeModal
              show={
                modalType === ModalTypes.MaintenanceCountdown ||
                (modalType === ModalTypes.Maintenance && !maintenanceNotified)
              }
              onHide={() => dispatch(showModal())}
            />
            <InactivityModal
              show={modalType === ModalTypes.Inactivity}
              onHide={() => dispatch(showModal())}
            />
          </>
        );
      }

      return (
        <>
          <NavRail />
          <OutletWrapper>{children || <Outlet />}</OutletWrapper>
        </>
      );
    } else if ((checkAdmin && !isAdmin) || (checkManager && !isManager)) {
      return <Navigate to="/" state={{ from: location }} replace />;
    }
  }
  return <Navigate to={`/login${location.search}`} state={{ from: location }} replace />;
};

export default RequireAuth;
